import { useState, useEffect, useMemo } from "react";
import { Menu, X, ChevronDown, ChevronRight, Phone, Mail, Building, Users, GraduationCap, Settings, BookOpen, Award, Library, Database } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const staticSchoolsData = [
  {
    name: "School of Engineering",
    aliases: ["School of Engineering Technology"],
    icon: Building,
    color: "text-blue-600",
    departments: [
      { name: "Agricultural and Bio-Environmental Engineering", url: "/agricultural-and-bio-environmental-engineering" },
      { name: "Chemical Engineering", url: "/chemical-engineering" },
      { name: "Civil Engineering", url: "/civil-engineering" },
      { name: "Computer Engineering", url: "/computer-engineering" },
      { name: "Electrical Engineering", url: "/electrical-engineering" },
      { name: "Mechanical Engineering", url: "/mechanical-engineering" },
      { name: "Renewable Energy Engineering", url: "/renewable-energy-engineering" },
    ],
  },
  {
    name: "School of Agricultural Technology",
    aliases: ["School of Agriculture", "School of Agriculture and Agricultural Technology"],
    icon: Building,
    color: "text-emerald-600",
    departments: [
      { name: "Agricultural Technology", url: "/agricultural-technology" },
      { name: "Crop Production Technology", url: "/crop-production-technology" },
      { name: "Horticulture and Landscape Technology", url: "/horticulture-and-landscape-technology" },
      { name: "Livestock and Animal Production Technology", url: "/livestock-and-animal-production-technology" },
    ],
  },
  {
    name: "School of Basic and General Studies",
    aliases: ["School of General Studies", "School of Preliminary Studies", "School of Basic Studies"],
    icon: BookOpen,
    color: "text-orange-600",
    departments: [
      { name: "Languages", url: "/languages" },
      { name: "Legal Studies", url: "/legal-studies" },
      { name: "Social Sciences", url: "/social-sciences" },
      { name: "Mathematics", url: "/mathematics" },
    ],
  },
  {
    name: "School of Financial Studies",
    aliases: ["School of Finance", "School of Financial Management Studies"],
    icon: Database,
    color: "text-purple-600",
    departments: [
      { name: "Accountancy", url: "/accountancy" },
      { name: "Banking and Finance", url: "/banking-and-finance" },
      { name: "Taxation", url: "/taxation" },
    ],
  },
  {
    name: "School of Business Administration and Management",
    aliases: ["School of Business and Management Studies", "School of Business Management"],
    icon: Building,
    color: "text-red-600",
    departments: [
      { name: "Business Administration and Management", url: "/business-administration-and-management" },
      { name: "Marketing", url: "/marketing" },
      { name: "Public Administration", url: "/public-administration" },
      { name: "Procurement and Supply Chain Management", url: "/procurement-and-supply-chain-management" },
    ],
  },
  {
    name: "School of Information Management Technology",
    aliases: ["School of Information Management", "School of Management Information Technology"],
    icon: Library,
    color: "text-cyan-600",
    departments: [
      { name: "Office Technology Management", url: "/office-of-technology-management" },
      { name: "Library and Information Science", url: "/library-and-information-science" },
      { name: "Health Information Management Technology", url: "/health-information-management-technology" },
      { name: "Mass Communication", url: "/mass-communication" },
    ],
  },
  {
    name: "School of Computing Technology",
    aliases: ["School of ICT", "School of Information and Communication Technology", "School of Computer Science"],
    icon: GraduationCap,
    color: "text-indigo-600",
    departments: [
      { name: "Artificial Intelligence Technology", url: "/artificial-intelligence-technology" },
      { name: "Computer Science", url: "/computer-science" },
      { name: "Cyber Security and Data Protection", url: "/cyber-security-and-data-protection" },
      { name: "Networking and Cloud Computing", url: "/networking-and-cloud-computing" },
      { name: "Software and Web Development", url: "/software-and-web-development" },
    ],
  },
  {
    name: "School of Applied & Natural Science",
    aliases: ["School of Applied and Natural Sciences", "School of Applied Natural Sciences"],
    icon: Building,
    color: "text-orange-600",
    departments: [
      { name: "Statistics", url: "/statistics" },
      { name: "Hospitality Management", url: "/hospitality-management" },
      { name: "Nutrition and Dietetics", url: "/nutrition-and-dietetics" },
      { name: "Food Science and Technology", url: "/food-science-and-technology" },
      { name: "Tourism Management Technology", url: "/tourism-management-technology" },
      { name: "Science Laboratory Technology", url: "/science-laboratory-technology" },
      { name: "Science Laboratory Technology (Biological Sciences)", url: "/science-laboratory-technology-biological-sciences-" },
      { name: "Science Laboratory Technology (Chemical Sciences)", url: "/science-laboratory-technology-chemical-sciences-" },
      { name: "Science Laboratory Technology (Physics)", url: "/science-laboratory-technology-physics-" },
    ],
  },
  {
    name: "School of Environmental Studies",
    aliases: ["School of Environmental Technology"],
    icon: Building,
    color: "text-green-600",
    departments: [
      { name: "Environmental Science and Management", url: "/environmental-science-and-management" },
      { name: "Estate Management and Valuation", url: "/estate-management-and-valuation" },
      { name: "Urban and Regional Planning", url: "/urban-and-regional-planning" },
      { name: "Architectural Technology", url: "/architectural-technology" },
      { name: "Quantity Surveying", url: "/quantity-surveying" },
      { name: "Building Technology", url: "/building-technology" },
      { name: "Surveying and Geoinformatics", url: "/surveying-and-geoinformatics" },
    ],
  },
  {
    name: "School of Communication and Media Studies",
    aliases: ["School of Communication", "School of Media and Communication Studies"],
    icon: Users,
    color: "text-pink-600",
    departments: [
      { name: "Film and Media Studies", url: "/film-and-media-studies" },
      { name: "Journalism and Media Studies", url: "/journalism-and-media-studies" },
      { name: "Strategic Communication and Media Studies", url: "/strategic-communication-and-media-studies" },
    ],
  },
];

const staticCentersData = {
  centres: [
    { name: "Career Development Center", url: "/career-development-center" },
    { name: "Entrepreneurship Center", url: "/enterpreneurship-center" },
    { name: "Center for Open Distance & e-Learning (CODeL)", url: "/center-for-open-distance-e-learning-codel" },
    { name: "Medical Center", url: "/medical-center" },
    { name: "Climate Change & Environmental Sustainability Center", url: "/climate-change-environmental-sustainability-center" },
    { name: "University Affiliation Center", url: "/university-affiliation-center" },
  ],
  units: [
    { name: "Anti-Corruption and Transparency Monitoring Unit (ACTU)", url: "/anti-corruption-and-transparency-monitoring-unit-actu-" },
    { name: "Servicom Unit", url: "/servicom-unit" },
    { name: "Registry Unit", url: "/registry-unit" },
    { name: "Bursary Unit", url: "/bursary-unit" },
    { name: "Rectory Unit", url: "/rectory-unit" },
    { name: "Polytechnic Legal Unit", url: "/polytechnic-legal-unit" },
    { name: "Security and Related Matters Unit", url: "/security-unit" },
  ],
  directorates: [
    { name: "Directorate of Communication and Related Matters", url: "/directorate-of-communication-and-related-matters" },
    { name: "Directorate of Continuing Education (DCE)", url: "/directorate-of-continuing-education-dce-" },
    { name: "Directorate of Gender Issues", url: "/directorate-of-gender-issues" },
    { name: "Directorate of Information and Technological Services (ITS)", url: "/directorate-of-information-and-technological-services-its-" },
    { name: "Directorate of Public Procurement", url: "/directorate-of-public-procurement" },
    { name: "Directorate of TETFund Intervention and Other Grants", url: "/directorate-of-tetfund-intervention-and-other-grants" },
    { name: "Directorate of Works and Services", url: "/directorate-of-works-and-services" },
    { name: "Directorate for Technical and Vocational Education Training", url: "/directorate-for-teaching-and-vocational-education-and-training" },
    { name: "Directorate of Physical Planning and Development (PPD)", url: "/directorate-of-physical-planning-and-development-ppd-" },
    { name: "Directorate of Academic Planning Unit", url: "/directorate-of-academic-planning-unit" },
    { name: "Directorate of Student Industrial Placement Unit (IPU)", url: "/directorate-of-student-industrial-placement-unit-ipu-" },
  ],
};

const resourcesData = {
  library: [
    "Digital Library",
    "Physical Library",
    "Research Collection",
    "E-Books & Journals",
    "Thesis & Dissertations",
    "Reference Materials",
  ],
  repository: [
    { label: "Institutional Repository", url: "http://repository.fedpolybida.edu.ng:4000/" },
    { label: "Polymath Journal", url: "/polymath-journal" },
    { label: "Research Papers", url: "/research-papers" },
    { label: "Student Projects", url: "/student-projects" },
    { label: "Conference Proceedings", url: "/conference-proceedings" },
  ],
};

const slugify = (name) =>
  String(name || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const fallbackPath = (type, name) => {
  const typePath = {
    department: "departments",
    center: "centers",
    unit: "units",
    directorate: "directorates",
  }[type] || "departments";
  return `/${typePath}/${slugify(name)}`;
};

const schoolAliasMap = new Map();
staticSchoolsData.forEach((school) => {
  [school.name, ...(school.aliases || [])].forEach((alias) => {
    schoolAliasMap.set(slugify(alias), school.name);
  });
});

function normalizeSchoolName(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'Unassigned Departments';
  return schoolAliasMap.get(slugify(raw)) || raw;
}

function departmentName(dept) {
  return typeof dept === 'string' ? dept : dept.name;
}

function departmentUrl(dept, type = 'department') {
  return typeof dept === 'object' && dept.url ? dept.url : fallbackPath(type, departmentName(dept));
}

function normalizeCmsPage(item, type) {
  return {
    id: item.id || `${type}-${item.slug || item.name}`,
    name: item.name,
    slug: item.slug,
    type,
    group: type === "department" ? normalizeSchoolName(item.parent_group || item.school || item.group || "Unassigned Departments") : (item.parent_group || item.school || item.group || ""),
    description: item.short_description || item.description || "",
    url: item.public_url || fallbackPath(type, item.slug || item.name),
    featured: Number(item.is_featured || 0) === 1,
  };
}

function fallbackPages(type) {
  if (type === "department") {
    return staticSchoolsData.flatMap((school) =>
      school.departments.map((dept) => normalizeCmsPage({
        name: departmentName(dept),
        parent_group: school.name,
        public_url: departmentUrl(dept, "department"),
      }, "department"))
    );
  }
  if (type === "center") {
    return staticCentersData.centres.map((item) => normalizeCmsPage({ name: departmentName(item), public_url: departmentUrl(item, "center") }, "center"));
  }
  if (type === "unit") {
    return staticCentersData.units.map((item) => normalizeCmsPage({ name: departmentName(item), public_url: departmentUrl(item, "unit") }, "unit"));
  }
  if (type === "directorate") {
    return staticCentersData.directorates.map((item) => normalizeCmsPage({ name: departmentName(item), public_url: departmentUrl(item, "directorate") }, "directorate"));
  }
  return [];
}

function canonicalPageKey(item) {
  const alias = slugify(item.name)
    .replace(/^enterpreneurship-/, 'entrepreneurship-')
    .replace(/^center-for-open-distance-and-e-learning-codel$/, 'center-for-open-distance-and-e-learning-codel');
  return `${item.type}-${item.slug || alias}`;
}

function dedupePages(items) {
  const map = new Map();
  items.filter((item) => item?.name).forEach((item) => {
    const key = canonicalPageKey(item);
    const existing = map.get(key);
    if (!existing) map.set(key, item);
    else if (!existing.featured && item.featured) map.set(key, item);
  });
  return [...map.values()].sort((a, b) => {
    if (a.featured !== b.featured) return a.featured ? -1 : 1;
    return String(a.name).localeCompare(String(b.name));
  });
}

function useInstitutionalNavigation() {
  const [pages, setPages] = useState({
    department: fallbackPages("department"),
    center: fallbackPages("center"),
    unit: fallbackPages("unit"),
    directorate: fallbackPages("directorate"),
  });

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const types = ["department", "center", "unit", "directorate"];
        const responses = await Promise.allSettled(
          types.map(async (type) => {
            const res = await fetch(`${API_BASE}/api/institutional-pages?type=${type}`);
            if (!res.ok) throw new Error(`Failed to load ${type}`);
            const data = await res.json();
            return [type, Array.isArray(data) ? data.map((item) => normalizeCmsPage(item, type)) : []];
          })
        );

        if (!alive) return;
        const next = {
          department: fallbackPages("department"),
          center: fallbackPages("center"),
          unit: fallbackPages("unit"),
          directorate: fallbackPages("directorate"),
        };

        responses.forEach((result) => {
          if (result.status === "fulfilled") {
            const [type, items] = result.value;
            next[type] = dedupePages([...items, ...next[type]]);
          }
        });

        setPages(next);
      } catch {
        // Keep static fallback if API is not available.
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  const schools = useMemo(() => {
    const groups = new Map();

    // Seed official school order first so the mega menu is always complete and stable.
    staticSchoolsData.forEach((school) => {
      groups.set(school.name, {
        name: school.name,
        departments: [],
        icon: school.icon,
        color: school.color,
        order: groups.size,
      });
    });

    pages.department.forEach((dept) => {
      const schoolName = normalizeSchoolName(dept.group || dept.parent_group || dept.school);
      if (!groups.has(schoolName)) {
        groups.set(schoolName, {
          name: schoolName,
          departments: [],
          icon: Building,
          color: "text-blue-600",
          order: groups.size,
        });
      }

      const target = groups.get(schoolName);
      const key = dept.slug || slugify(dept.name);
      const existingIndex = target.departments.findIndex((item) => (item.slug || slugify(item.name)) === key);
      if (existingIndex >= 0) target.departments[existingIndex] = dept;
      else target.departments.push(dept);
    });

    return [...groups.values()]
      .map((school) => ({
        ...school,
        departments: school.departments.sort((a, b) => String(a.name).localeCompare(String(b.name))),
      }))
      .filter((school) => school.departments.length)
      .sort((a, b) => a.order - b.order);
  }, [pages.department]);

  return {
    schools,
    centers: pages.center,
    units: pages.unit,
    directorates: pages.directorate,
  };
}

function DesktopNavLink({ href, children, scrolled }) {
  return (
    <a
      href={href}
      className={`relative px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105 group ${
        scrolled
          ? "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
          : "text-white hover:text-yellow-300 hover:bg-white/10"
      }`}
    >
      {children}
      <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
    </a>
  );
}

const accentClasses = {
  blue: "hover:bg-blue-50 hover:text-blue-600",
  green: "hover:bg-green-50 hover:text-green-600",
  purple: "hover:bg-purple-50 hover:text-purple-600",
};

function MegaItem({ item, accent = "blue", onClick }) {
  return (
    <a
      href={item.url}
      onClick={onClick}
      className={`block rounded-lg px-3 py-2 text-sm text-gray-600 transition-all duration-200 ${accentClasses[accent] || accentClasses.blue}`}
    >
      <span className="font-semibold">{item.name}</span>
      {item.description ? <span className="block truncate text-xs text-gray-400">{item.description}</span> : null}
    </a>
  );
}

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navData = useInstitutionalNavigation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest("nav")) setIsOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  const closeMobile = () => setIsOpen(false);

  return (
    <header className="fpb-site-header fixed left-0 right-0 top-0 z-50 w-full">
      <div
        className={`relative z-[90] hidden items-center justify-between overflow-visible px-6 py-3 text-sm text-gray-100 transition-all duration-500 lg:flex ${
          scrolled
            ? "bg-gradient-to-r from-blue-950/90 via-blue-900/90 to-indigo-900/90 backdrop-blur-sm"
            : "bg-gradient-to-r from-blue-950 via-blue-900 to-indigo-900"
        } border-b border-blue-800/30`}
      >
        <div className="flex items-center space-x-8">
          <div className="flex items-center gap-2 transition hover:text-yellow-300">
            <Phone size={14} />
            <span>+234 801 234 5678</span>
          </div>
          <div className="flex items-center gap-2 transition hover:text-yellow-300">
            <Mail size={14} />
            <span>info@fedpolybida.edu.ng</span>
          </div>
        </div>
        <div className="flex items-center space-x-6">
          <a href="/tetfund-projects" className="rounded px-2 py-1 transition hover:text-yellow-300">Tetfund</a>
          <a href="/portal" className="rounded px-2 py-1 transition hover:text-yellow-300">Portal</a>
          <a href="/poly-consult" className="rounded px-2 py-1 transition hover:text-yellow-300">Enterprises</a>
        </div>
      </div>

      <nav
        className={`fpb-site-nav relative z-[70] overflow-visible transition-all duration-500 ${
          scrolled ? "bg-white/95 shadow-2xl backdrop-blur-lg border-b border-gray-200" : "bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 shadow-lg"
        }`}
      >
        <div className="fpb-site-header-inner mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-3 sm:px-6 sm:py-4">
          <div className="fpb-site-header-brand flex min-w-0 flex-1 items-center overflow-hidden pr-1">
            <a href="/" className="flex max-w-full min-w-0 items-center overflow-hidden">
              <img src="/images/fpb.png" alt="Federal Polytechnic Bida Logo" className="h-9 w-9 flex-shrink-0 object-contain transition-transform hover:scale-105 sm:h-12 sm:w-12" />
              <span
                className={`fpb-site-header-title ml-2 text-base font-bold leading-tight tracking-wide transition-all duration-300 sm:text-2xl ${
                  scrolled ? "bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent" : "text-white hover:text-yellow-300"
                }`}
              >
                The Federal Polytechnic Bida
              </span>
            </a>
          </div>

          <div className="hidden items-center space-x-1 font-medium lg:flex">
            <DesktopNavLink href="/" scrolled={scrolled}>Home</DesktopNavLink>

            <div className="relative group" onMouseEnter={() => setActiveDropdown("centers")} onMouseLeave={() => setActiveDropdown(null)}>
              <button className={`flex items-center rounded-lg px-4 py-2 transition-all duration-300 hover:scale-105 ${scrolled ? "text-gray-700 hover:bg-blue-50 hover:text-blue-600" : "text-white hover:bg-white/10 hover:text-yellow-300"}`}>
                Centers <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${activeDropdown === "centers" ? "rotate-180" : ""}`} />
              </button>
              <div className={`absolute left-1/2 top-full z-[9999] mt-2 w-[1060px] max-w-[96vw] -translate-x-1/2 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl transition-all duration-300 ${activeDropdown === "centers" ? "visible translate-y-0 opacity-100" : "invisible translate-y-2 opacity-0"}`}>
                <div className="max-h-[calc(100vh-185px)] overflow-y-auto p-6">
                  <div className="mb-4 flex items-center justify-between gap-4 border-b border-slate-200 pb-3">
                    <div>
                      <h3 className="text-base font-black text-slate-900">Centres, Units and Directorates</h3>
                      <p className="text-xs font-semibold text-slate-500">Complete official structure plus active CMS-created pages.</p>
                    </div>
                    <a href="/centers" className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-black text-blue-700">All Centres</a>
                  </div>
                  <div className="grid grid-cols-3 gap-5">
                  <div>
                    <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2"><Settings size={20} className="text-blue-600" /><h3 className="font-bold text-gray-800">Centres</h3></div>
                    <div className="space-y-1">
                      {navData.centers.map((item) => <MegaItem key={item.id} item={item} accent="blue" />)}
                      <a href="/centers" className="mt-2 block rounded-lg bg-blue-50 px-3 py-2 text-sm font-black text-blue-700">View all Centres →</a>
                    </div>
                  </div>
                  <div>
                    <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2"><BookOpen size={20} className="text-green-600" /><h3 className="font-bold text-gray-800">Units</h3></div>
                    <div className="space-y-1">
                      {navData.units.map((item) => <MegaItem key={item.id} item={item} accent="green" />)}
                      <a href="/units" className="mt-2 block rounded-lg bg-green-50 px-3 py-2 text-sm font-black text-green-700">View all Units →</a>
                    </div>
                  </div>
                  <div>
                    <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2"><Award size={20} className="text-purple-600" /><h3 className="font-bold text-gray-800">Directorates</h3></div>
                    <div className="space-y-1">
                      {navData.directorates.map((item) => <MegaItem key={item.id} item={item} accent="purple" />)}
                      <a href="/directorates" className="mt-2 block rounded-lg bg-purple-50 px-3 py-2 text-sm font-black text-purple-700">View all Directorates →</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>

            <div className="relative group" onMouseEnter={() => setActiveDropdown("academics")} onMouseLeave={() => setActiveDropdown(null)}>
              <button className={`flex items-center rounded-lg px-4 py-2 transition-all duration-300 hover:scale-105 ${scrolled ? "text-gray-700 hover:bg-blue-50 hover:text-blue-600" : "text-white hover:bg-white/10 hover:text-yellow-300"}`}>
                Academics <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${activeDropdown === "academics" ? "rotate-180" : ""}`} />
              </button>
              <div className={`absolute left-1/2 top-full z-[9999] mt-2 w-[1120px] max-w-[96vw] -translate-x-1/2 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl transition-all duration-300 ${activeDropdown === "academics" ? "visible translate-y-0 opacity-100" : "invisible translate-y-2 opacity-0"}`}>
                <div className="p-6">
                  <div className="mb-4 flex items-center justify-between gap-4 border-b border-slate-200 pb-3">
                    <div>
                      <h3 className="text-base font-black text-slate-900">Schools and Departments</h3>
                      <p className="text-xs font-semibold text-slate-500">Departments created by admin appear under their selected School.</p>
                    </div>
                    <a href="/departments" className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-black text-blue-700">All Departments</a>
                  </div>
                  <div className="grid max-h-[76vh] grid-cols-4 gap-4 overflow-y-auto pr-2">
                    {navData.schools.map((school) => {
                      const Icon = school.icon;
                      return (
                        <div key={school.name} className="rounded-2xl bg-slate-50/80 p-4 shadow-sm ring-1 ring-slate-100">
                          <div className="mb-2 flex items-center gap-2 border-b border-gray-200 pb-2">
                            <Icon size={18} className={school.color} />
                            <h3 className="text-sm font-bold leading-tight text-gray-800">{school.name}</h3>
                          </div>
                          {school.departments.map((dept) => (
                            <MegaItem key={dept.id} item={dept} accent="blue" />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                  <a href="/departments" className="mt-4 inline-flex rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white">View all Departments →</a>
                </div>
              </div>
            </div>

            <DesktopNavLink href="/about" scrolled={scrolled}>About</DesktopNavLink>

            <div className="relative group" onMouseEnter={() => setActiveDropdown("resources")} onMouseLeave={() => setActiveDropdown(null)}>
              <button className={`flex items-center rounded-lg px-4 py-2 transition-all duration-300 hover:scale-105 ${scrolled ? "text-gray-700 hover:bg-blue-50 hover:text-blue-600" : "text-white hover:bg-white/10 hover:text-yellow-300"}`}>
                Resources <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${activeDropdown === "resources" ? "rotate-180" : ""}`} />
              </button>
              <div className={`absolute left-1/2 top-full z-[9999] mt-2 w-[520px] max-w-[95vw] -translate-x-1/2 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl transition-all duration-300 ${activeDropdown === "resources" ? "visible translate-y-0 opacity-100" : "invisible translate-y-2 opacity-0"}`}>
                <div className="grid grid-cols-2 gap-6 p-6">
                  <div>
                    <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2"><Library size={20} className="text-blue-600" /><h3 className="font-bold text-gray-800">Library</h3></div>
                    {resourcesData.library.map((item) => <a key={item} href={`/${slugify(item)}`} className="block rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-600">{item}</a>)}
                  </div>
                  <div>
                    <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2"><Database size={20} className="text-green-600" /><h3 className="font-bold text-gray-800">Repository</h3></div>
                    {resourcesData.repository.map((item) => <a key={item.label} href={item.url} target={item.url.startsWith("http") ? "_blank" : "_self"} rel="noreferrer" className="block rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-green-50 hover:text-green-600">{item.label}</a>)}
                  </div>
                </div>
              </div>
            </div>

            {["Admissions", "News", "Contact"].map((link) => (
              <DesktopNavLink key={link} href={`/${link.toLowerCase()}`} scrolled={scrolled}>{link}</DesktopNavLink>
            ))}
          </div>

          <button
            className={`fpb-site-header-menu relative z-20 ml-auto flex-shrink-0 rounded-lg p-2 transition-all duration-100 hover:scale-110 lg:hidden ${scrolled ? "text-gray-700 hover:bg-gray-100" : "text-white hover:bg-white/10"}`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={25} /> : <Menu size={30} />}
          </button>
        </div>

        <div className={`lg:hidden bg-white/95 backdrop-blur-lg border-t border-gray-200 transition-all duration-500 ease-in-out ${isOpen ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}>
          <div className="max-h-[70vh] space-y-2 overflow-y-auto px-4 py-4 sm:px-6">
            <a href="/" onClick={closeMobile} className="block rounded-lg px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600">Home</a>

            <div>
              <button onClick={() => setMobileDropdown(mobileDropdown === "centers" ? null : "centers")} className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600">
                <span className="font-medium">Centers, Units & Directorates</span>
                <ChevronDown className={`h-5 w-5 transition-transform ${mobileDropdown === "centers" ? "rotate-180" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${mobileDropdown === "centers" ? "mt-2 max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                <div className="max-h-80 space-y-4 overflow-y-auto rounded-lg bg-gray-50 p-4">
                  {[
                    ["Centres", navData.centers, "text-blue-600"],
                    ["Units", navData.units, "text-green-600"],
                    ["Directorates", navData.directorates, "text-purple-600"],
                  ].map(([title, items, color]) => (
                    <div key={title}>
                      <h4 className={`mb-2 font-medium ${color}`}>{title}</h4>
                      <div className="space-y-1">
                        {items.map((item) => (
                          <a key={item.id} href={item.url} onClick={closeMobile} className="block py-1 pl-2 text-xs text-gray-600 hover:text-blue-600">• {item.name}</a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <button onClick={() => setMobileDropdown(mobileDropdown === "schools" ? null : "schools")} className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600">
                <span className="font-medium">Academics</span>
                <ChevronDown className={`h-5 w-5 transition-transform ${mobileDropdown === "schools" ? "rotate-180" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${mobileDropdown === "schools" ? "mt-2 max-h-[70vh] opacity-100" : "max-h-0 opacity-0"}`}>
                <div className="max-h-[64vh] overflow-y-auto rounded-lg bg-gray-50 p-4">
                  {navData.schools.map((school) => {
                    const Icon = school.icon;
                    return (
                      <div key={school.name} className="mb-3 rounded-lg bg-white p-3 shadow-sm">
                        <div className="mb-2 flex items-center gap-2">
                          <Icon size={16} className={school.color} />
                          <p className="text-sm font-medium text-gray-800">{school.name}</p>
                        </div>
                        {school.departments.map((dept) => (
                          <a key={dept.id} href={dept.url} onClick={closeMobile} className="block py-1 pl-2 text-xs text-gray-600 hover:text-blue-600">• {dept.name}</a>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {["About", "Resources", "Admissions", "News", "Contact"].map((link) => (
              <a key={link} href={`/${link.toLowerCase()}`} onClick={closeMobile} className="block rounded-lg px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600">{link}</a>
            ))}
            <a href="/tetfund-projects" onClick={closeMobile} className="block rounded-lg px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600">Tetfund</a>
            <a href="/portal" onClick={closeMobile} className="block rounded-lg px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600">Portal</a>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
